import './styles.scoped.css'
import {useEffect, useState, useRef} from 'react'
import {
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Link,
    Avatar,
    Button,
    Autocomplete,
    IconButton,
    Chip,
    Skeleton,
} from '@mui/material'
import {UploadFile, Upload, Edit, Delete, Cancel} from '@mui/icons-material'
import {Controller} from 'react-hook-form'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {useDropzone} from 'react-dropzone'
import {useMobile} from '../../../../hooks'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {accountApi, caregiverApi} from '../../../../services'
import {useParams} from 'react-router-dom'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {format} from 'date-fns'
import {parseError, ignoreTimezone, emailRegex} from '../../../../utils/helpers'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton, TextSkeleton} from '../../../../components'

const DemographicsSkeleton = ({mobile}) => (
    <SkeletonContainer>
        <FieldSkeleton labelWidth={110} />
        <div className="selected-items-container">
            <Skeleton variant="rounded" height={40} sx={{borderRadius: '100px'}} />
        </div>
        <br />
        <hr className="line" />
        <br />
        <TextSkeleton width={90} fontSize={12} />
        {mobile ? <ButtonSkeleton width={150} /> : <Skeleton variant="rounded" width="50%" height={150} sx={{minWidth: 300, borderRadius: 0.25}} />}
        <br />
        <div className="form" style={{marginBottom: 0}}>
            <FieldSkeleton labelWidth={75} sx={{flex: 1}} />
            <FieldSkeleton labelWidth={75} sx={{flex: 1}} />
        </div>
        <br />
        <FieldSkeleton labelWidth={40} />
        <br />
        <FieldSkeleton labelWidth={45} />
        <br />
        <FieldSkeleton labelWidth={55} />
        <br />
        <FieldSkeleton labelWidth={30} />
        <br />
        <FieldSkeleton labelWidth={85} />
        <br />
        <FieldSkeleton labelWidth={85} />
        <br />
        <FieldSkeleton labelWidth={60} />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={130} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const [branches, setBranches] = useState([])
    const [selectedBranches, setSelectedBranches] = useState([])
    // The masked SSN placeholder currently sitting in the field. A ref, not
    // state: it must be read at submit/validation time regardless of when the
    // yup schema or submit closure was created, and it never drives a render
    // itself (form.setValue does that). hasSsnOnFile is the render-facing
    // twin of this — mutating a ref doesn't trigger a re-render, so anything
    // that affects what's displayed needs real state instead.
    const maskedSsnRef = useRef('')
    const [hasSsnOnFile, setHasSsnOnFile] = useState(false)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    // The placements need the branch list, so the skeleton waits for it as well as the caregiver.
    const branchesLoaded = useRef(false)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                branch_ids: yup.array().of(yup.string()),
                branch_ids_helper: selectedBranches?.length > 0 ? yup.string().notRequired() : yup.string().required('Branches is required'),
                primary_branch_id: yup.string(),
                firstName: yup.string().required('First Name is required'),
                lastName: yup.string().required('Last Name is required'),
                initial: yup.string(),
                email: yup
                    .string()
                    .required('Email is required')
                    .test('is-valid-email', 'Invalid email address', value => emailRegex.test(value)),
                gender: yup.string().required('Gender is required'),
                // The server never returns the caregiver's actual SSN, only its last
                // four digits — the field is pre-filled with a masked placeholder built
                // from those (see maskedSsnRef/reset below). Leaving that placeholder
                // untouched must validate and must not overwrite the real value, so this
                // accepts either the current placeholder as-is or a genuine new 9-digit
                // number — never a hand-edited partial mask.
                ssn: yup.string().test('ssn-optional', 'SSN must be 9 digits', value => {
                    if (!value || value.trim() === '') return true
                    if (value === maskedSsnRef.current) return true
                    return /^\d{3}-?\d{2}-?\d{4}$/.test(value)
                }),
                dateOfBirth: yup.date().required('Date of Birth is required'),
                maritalStatus: yup.string(),
                ethnicity: yup.string(),
            }),
        ),
    })
    const [file, setFile] = useState(null)
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setFile(acceptedFiles[0])
            setAvatarUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })
    const [avatarUrl, setAvatarUrl] = useState(null)

    const genders = [
        {key: 'male', value: 'Male'},
        {key: 'female', value: 'Female'},
        {key: 'other', value: 'Other'},
        {key: 'prefer_not_to_say', value: 'Prefer Not to Say'},
    ]

    const maritalStatus = [
        {key: 'married', value: 'Married'},
        {key: 'single', value: 'Single'},
        {key: 'divorced', value: 'Divorced'},
        {key: 'widowed', value: 'Widowed'},
        {key: 'separated', value: 'Separated'},
    ]

    const ethnicities = [
        {key: 'africa', value: 'Africa'},
        {key: 'asia', value: 'Asia'},
        {key: 'europe', value: 'Europe'},
    ]

    useEffect(() => {
        accountApi
            .getBranches()
            .then(
                ({
                    data: {
                        data: {branches},
                    },
                }) => {
                    branchesLoaded.current = true
                    setBranches(branches)
                },
            )
            .catch(() => {
                branchesLoaded.current = true
                setBranches([])
            })
    }, [])

    useEffect(() => {
        reset(true)
    }, [branches])

    useEffect(() => {
        form.setValue(
            'branch_ids',
            selectedBranches.map(lang => lang.id),
        )
        if (selectedBranches?.length == 1 && !selectedBranches[0].is_primary) {
            selectAsPrimary(selectedBranches[0].id)
            form.setValue('primary_branch_id', selectedBranches[0].id)
        } else {
            form.setValue('primary_branch_id', selectedBranches.find(a => a.is_primary)?.id)
        }
    }, [selectedBranches])

    useEffect(() => {
        if (!file) return
        const formData = new FormData()
        formData.append('file', file)
        setLoading(true)
        caregiverApi
            .uploadCaregiverPhoto(branchId, caregiverId, formData)
            .then(({data}) => {
                setLoading(false)
                setAvatarUrl(data?.avatar_url || null)
            })
            .catch(error => {
                setLoading(false)
                setFile(null)
                setAvatarUrl(null)
                const {title, description} = parseError(error)
                setMessageBox({status: 'error', title: title || 'Photo Upload Failed', description})
            })
    }, [file])

    const removeFile = async () => {
        setLoading(true)
        caregiverApi
            .removeCaregiverPhoto(branchId, caregiverId)
            .then(() => {
                setLoading(false)
                setFile(null)
                setAvatarUrl(null)
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({status: 'error', title: title || 'Photo Removal Failed', description})
            })
    }

    const selectAsPrimary = id => {
        setSelectedBranches(prev =>
            prev.map(b => ({
                ...b,
                is_primary: b.id === id,
            })),
        )
    }

    const deleteItem = id => {
        setSelectedBranches(prev => {
            const updated = prev.filter(b => b.id !== id)
            const wasPrimary = prev.find(b => b.id === id)?.is_primary
            if (wasPrimary && updated?.length > 0) {
                updated[0].is_primary = true
            }
            return updated
        })
    }

    // The initial loads (before and after the branch list arrives) show the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        const id = ++requestId.current
        if (!initial) setLoading(true)
        caregiverApi
            .getBranchCaregiver(branchId, caregiverId)
            .then(({data}) => {
                if (!initial) setLoading(false)
                if (id !== requestId.current) return
                if (branchesLoaded.current) setSkeleton(false)
                const temp = []
                data?.branches?.forEach(val => {
                    temp.push({...branches.find(a => a.id == val?.branch_id), is_primary: val?.is_primary})
                })
                setSelectedBranches(temp)
                setAvatarUrl(data?.avatar_url || null)
                maskedSsnRef.current = data?.ssn_last_four ? `***-**-${data.ssn_last_four}` : ''
                setHasSsnOnFile(!!data?.ssn_last_four)
                form.setValue('ssn', maskedSsnRef.current)
                form.setValue('firstName', data?.first_name)
                form.setValue('lastName', data?.last_name)
                form.setValue('initial', data?.initial)
                form.setValue('email', data?.email)
                form.setValue('gender', data?.gender)
                form.setValue('dateOfBirth', new Date(ignoreTimezone(data?.date_of_birth)))
                form.setValue('maritalStatus', data?.marital_status)
                form.setValue('ethnicity', data?.ethnicity)
            })
            .catch(error => {
                if (!initial) setLoading(false)
                if (id !== requestId.current) return
                if (branchesLoaded.current) setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        try {
            setLoading(true)
            await caregiverApi.updateCaregiver(branchId, caregiverId, {
                first_name: data?.firstName,
                last_name: data?.lastName,
                initial: data?.initial,
                email: data?.email,
                gender: data?.gender,
                date_of_birth: data?.dateOfBirth ? format(new Date(data?.dateOfBirth), 'yyyy-MM-dd') : '',
                marital_status: data?.maritalStatus,
                ethnicity: data?.ethnicity,
                branch_ids: data?.branch_ids,
                primary_branch_id: data?.primary_branch_id,
                // Only send ssn when it's a real new value — the field is pre-filled
                // with a masked placeholder (see maskedSsnRef), and re-sending that
                // placeholder verbatim would overwrite the stored SSN with garbage.
                ...(data?.ssn && data.ssn !== maskedSsnRef.current ? {ssn: data.ssn} : {}),
            })

            setMessageBox({
                status: 'success',
                title: 'Caregiver Informations Updated',
                description: `${data?.firstName}'s details have been successfully updated. All changes are now reflected in the system.`,
            })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {skeleton && <DemographicsSkeleton mobile={mobile} />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Placement{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <Autocomplete
                    multiple
                    options={branches}
                    value={selectedBranches}
                    onChange={(e, newVal) => setSelectedBranches(newVal)}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={opt => opt.branch_name}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Select Branches"
                            {...form.register('branch_ids_helper')}
                            error={!!form.formState.errors['branch_ids_helper']}
                            helperText={form.formState.errors['branch_ids_helper']?.message}
                        />
                    )}
                    slotProps={{chip: {sx: {display: 'none'}}}}
                />
                <div className="selected-items-container">
                    {selectedBranches?.map((item, index) => (
                        <div className="selected-items" key={index}>
                            <Typography variant="chipLabel" color="text.primary">
                                {item.branch_name}
                            </Typography>
                            <div className="selected-items-actions">
                                {item?.is_primary ? (
                                    <Chip label="Primary" color="primary" size="small" />
                                ) : (
                                    <Link href="#" underline="hover" color="primary" onClick={() => selectAsPrimary(item?.id)}>
                                        <Typography variant="chipLabel">Set as primary</Typography>
                                    </Link>
                                )}
                                <IconButton onClick={() => deleteItem(item?.id)}>
                                    <Cancel color="disabled" />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                </div>
                <br />
                <hr className="line" />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Profile Picture
                </Typography>
                <div style={{display: avatarUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                    <Avatar src={avatarUrl} style={{height: 130, width: 130}} />
                    <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                        Replace
                    </Button>
                    <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                        Remove
                    </Button>
                </div>
                <div
                    {...getRootProps()}
                    style={{
                        width: '50%',
                        backgroundColor: isDragActive ? '#e3f2fd' : '#ffffff',
                        display: avatarUrl || mobile ? 'none' : 'block',
                        borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                        border: '1px dashed #0000001F',
                        borderRadius: 1,
                        height: 150,
                        minWidth: 300,
                        boxSizing: 'border-box',
                        textAlign: 'center',
                    }}>
                    <input {...getInputProps()} />
                    <br />
                    <UploadFile style={{fontSize: 26}} color="primary" />
                    <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                        <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                            {'Click to upload '}
                        </Link>
                        or drag and drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        PNG, JPG, GIF or WebP (max. 3MB)
                    </Typography>
                </div>
                {!avatarUrl && mobile && (
                    <>
                        <br />
                        <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                            Upload Photo
                        </Button>
                        <br />
                    </>
                )}
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            First Name{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your first name"
                            fullWidth
                            {...form.register('firstName')}
                            error={!!form.formState.errors['firstName']}
                            helperText={form.formState.errors['firstName']?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            Last Name{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your last name"
                            fullWidth
                            {...form.register('lastName')}
                            error={!!form.formState.errors['lastName']}
                            helperText={form.formState.errors['lastName']?.message}
                        />
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Initial
                </Typography>
                <TextField placeholder="Enter your initial" fullWidth {...form.register('initial')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Email{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your email"
                    fullWidth
                    disabled
                    {...form.register('email')}
                    error={!!form.formState.errors['email']}
                    helperText={form.formState.errors['email']?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Gender{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <FormControl fullWidth error={!!form.formState.errors['gender']}>
                    <Controller
                        {...form.register('gender')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your gender</span>
                                    }
                                    return genders.find(item => item.key === selected)?.value ?? selected
                                }}>
                                {genders.map(item => (
                                    <MenuItem key={item?.key} value={item?.key}>
                                        {item?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <FormHelperText>{form.formState.errors['gender']?.message}</FormHelperText>
                </FormControl>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    SSN
                </Typography>
                <TextField
                    placeholder="Enter your SSN"
                    fullWidth
                    {...form.register('ssn')}
                    onFocus={e => {
                        // The field starts holding the masked placeholder as a real value
                        // (not just placeholder text) so it round-trips untouched when the
                        // admin isn't changing it. Select-all on focus so typing a real SSN
                        // replaces it instead of appending to "***-**-6789".
                        if (e.target.value === maskedSsnRef.current) {
                            e.target.select()
                        }
                    }}
                    error={!!form.formState.errors['ssn']}
                    helperText={
                        form.formState.errors['ssn']?.message ||
                        (hasSsnOnFile ? 'Leave as-is to keep the SSN on file, or type a new 9-digit SSN to replace it.' : undefined)
                    }
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Date of Birth{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <Controller
                    {...form.register('dateOfBirth')}
                    control={form.control}
                    render={({field}) => (
                        <DatePicker
                            placeholder="Enter your date of birth"
                            {...field}
                            value={field.value ?? null}
                            onChange={newValue => field.onChange(newValue)}
                            enableAccessibleFieldDOMStructure={false}
                            slots={{
                                textField: params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={!!form.formState.errors['dateOfBirth']}
                                        helperText={form.formState.errors['dateOfBirth']?.message}
                                    />
                                ),
                            }}
                        />
                    )}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Marital Status
                </Typography>
                <FormControl fullWidth>
                    <Controller
                        {...form.register('maritalStatus')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your marital status</span>
                                    }
                                    return maritalStatus.find(item => item.key === selected)?.value ?? selected
                                }}>
                                {maritalStatus.map(item => (
                                    <MenuItem key={item?.key} value={item?.key}>
                                        {item?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Ethnicity
                </Typography>
                <FormControl fullWidth>
                    <Controller
                        {...form.register('ethnicity')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your ethnicity</span>
                                    }
                                    return ethnicities.find(item => item.key === selected)?.value ?? selected
                                }}>
                                {ethnicities.map(item => (
                                    <MenuItem key={item?.key} value={item?.key}>
                                        {item?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
